/**
 * Document request naming subjects (primary + coborrowers).
 * Prefer legacy Redux for 0–1 coborrower (keep Add-document UI identical).
 * Use /los/v2 participants only when there are 2+ coborrowers.
 */

/** Match legacy DocumentChecklist suffix: `${firstname} ${lastname.charAt(0)}.` */
export const documentNameSuffix = (firstName, lastName) => {
    const first = `${firstName || ""}`.trim()
    const last = `${lastName || ""}`.trim()
    if (!first && !last) return ""
    if (!last) return first
    return `${first} ${last.charAt(0)}.`
}

export const documentNameWithSubject = (documentName, subject) => {
    const base = `${documentName || ""}`.trim()
    const suffix = subject?.suffix || documentNameSuffix(subject?.firstName, subject?.lastName)
    if (!base) return ""
    if (!suffix) return base
    return `${base} - ${suffix}`
}

export const subjectsFromParticipantsV2 = (participants = []) => {
    const list = Array.isArray(participants) ? participants : []
    return list
        .map((p) => {
            const firstName = `${p.firstName || p.FirstName || ""}`.trim()
            const lastName = `${p.lastName || p.LastName || ""}`.trim()
            const suffix = documentNameSuffix(firstName, lastName)
            if (!suffix) return null
            return {
                partyId: p.partyId || p.PartyID || "",
                isPrimary: !!(p.isPrimary || p.IsPrimary),
                firstName,
                lastName,
                fullName: [firstName, lastName].filter(Boolean).join(" "),
                suffix,
            }
        })
        .filter(Boolean)
}

/** Legacy Redux application.borrower / .coborrower — same people the old modal used. */
export const subjectsFromLegacyApplication = (application) => {
    if (!application) return []
    const subjects = []
    const b = application.borrower
    if (b) {
        const firstName = `${b.firstname || ""}`.trim()
        const lastName = `${b.lastname || ""}`.trim()
        const suffix = documentNameSuffix(firstName, lastName)
        if (suffix) {
            subjects.push({
                partyId: "",
                isPrimary: true,
                firstName,
                lastName,
                fullName: [firstName, lastName].filter(Boolean).join(" "),
                suffix,
            })
        }
    }
    if (`${application.hascoborrower || ""}` === "withcoborrower" && application.coborrower) {
        const c = application.coborrower
        const firstName = `${c.firstname || ""}`.trim()
        const lastName = `${c.lastname || ""}`.trim()
        const suffix = documentNameSuffix(firstName, lastName)
        if (suffix) {
            subjects.push({
                partyId: "",
                isPrimary: false,
                firstName,
                lastName,
                fullName: [firstName, lastName].filter(Boolean).join(" "),
                suffix,
            })
        }
    }
    return subjects
}

/**
 * Use V2 participants whenever they include coborrowers (for extra named document options).
 * Prefer full V2 list when it has more coborrowers than legacy; otherwise legacy.
 * Assign-to UI must NOT use this for labels — keep Redux application.borrower/coborrower there.
 */
export const fetchDocumentSubjects = async (loanId, application, token) => {
    const legacy = subjectsFromLegacyApplication(application)
    if (loanId && token) {
        try {
            const response = await fetch(`/los/v2/loans/${loanId}/participants`, {
                method: "GET",
                headers: {
                    Authorization: "Bearer " + token,
                    Cache: "no-cache",
                },
            })
            if (response.ok) {
                const body = await response.json()
                if (body.Status === "OK") {
                    const fromV2 = subjectsFromParticipantsV2(body.Participants || [])
                    const extraCo = fromV2.filter((s) => !s.isPrimary).length
                    const legacyCo = legacy.filter((s) => !s.isPrimary).length
                    if (extraCo > legacyCo) {
                        return fromV2
                    }
                    // Same 0–1 coborrower count: keep legacy names, copy partyId from V2 when possible.
                    if (fromV2.length > 0) {
                        return legacy.map((s) => {
                            const match = fromV2.find((v) => v.isPrimary === s.isPrimary && v.suffix === s.suffix)
                                || fromV2.find((v) => v.fullName === s.fullName)
                            return match ? { ...s, partyId: match.partyId } : s
                        })
                    }
                }
            }
        } catch (err) {
            console.warn("fetchDocumentSubjects: V2 participants failed, using legacy application", err)
        }
    }
    return legacy
}

/**
 * Assign-to borrower option label.
 * 0–1 coborrower: keep exact legacy Redux copy.
 * 2+ coborrowers: short form so the Select does not truncate a long name list.
 */
export const assignToBorrowersLabel = (subjects, application) => {
    const list = Array.isArray(subjects) ? subjects : []
    const primary = list.find((s) => s.isPrimary)
    const cos = list.filter((s) => !s.isPrimary)

    if (cos.length >= 2 && primary?.fullName) {
        return `${primary.fullName} & ${cos.length} coborrowers (Borrowers)`
    }

    if (application?.borrower) {
        const b = application.borrower
        const hasCo = `${application.hascoborrower || ""}` === "withcoborrower" && application.coborrower
        return `${b.firstname} ${b.lastname} ${hasCo ? `& ${application.coborrower.firstname} ${application.coborrower.lastname} ` : ""} (Borrower${hasCo ? "s" : ""})`
    }

    if (!primary?.fullName) return "Borrower"
    if (cos.length === 0) return `${primary.fullName} (Borrower)`
    return `${primary.fullName} & ${cos[0].fullName} (Borrowers)`
}

/** Prefer TaskName suffix (` - First L.`) so extra coborrowers are not matched by firstname alone. */
export const matchSubjectFromTaskName = (taskName, subjects) => {
    const name = `${taskName || ""}`
    const list = (Array.isArray(subjects) ? subjects : []).filter(Boolean)
    const bySuffix = [...list]
        .filter((s) => s.suffix)
        .sort((a, b) => `${b.suffix}`.length - `${a.suffix}`.length)
    for (const s of bySuffix) {
        if (name.includes(s.suffix)) return s
    }
    return null
}

/**
 * Which Redux 1003 slice this person maps to.
 * Extra coborrowers (beyond application.coborrower) have no slice — return "".
 */
export const reduxViewForSubject = (subject, application) => {
    if (!subject) return ""
    if (subject.isPrimary) return "borrower"
    const hasCo = `${application?.hascoborrower || ""}` === "withcoborrower" && application?.coborrower
    const legacyCoSuffix = hasCo
        ? `${application.coborrower.firstname} ${application.coborrower.lastname.charAt(0)}.`
        : ""
    if (legacyCoSuffix && subject.suffix === legacyCoSuffix) return "coborrower"
    return ""
}

/**
 * Resolve Smart Verify columns → partyIds for V2 split tables.
 * Mapping1003 uses borrower.* / coborrower.* paths; 3rd+ coborrowers compare via borrower.* only.
 */
export const resolveSmartVerifyParties = ({ app, subjects, taskName }) => {
    const byId = app?.participantsById || {}
    const order = Array.isArray(app?.participantOrder) ? app.participantOrder : Object.keys(byId)
    const primaryId = app?.primaryPartyId || order.find((id) => byId[id]?.isPrimary) || order[0] || ""
    const coborrowerIds = order.filter((id) => id && id !== primaryId && byId[id])
    const firstCoId = coborrowerIds[0] || ""
    const hasCo = `${app?.hascoborrower || ""}` === "withcoborrower" && coborrowerIds.length > 0

    const primaryPerson = byId[primaryId]
    const firstCoPerson = firstCoId ? byId[firstCoId] : null

    const viewApplication = {
        hascoborrower: hasCo ? "withcoborrower" : "",
        borrower: {
            firstname: primaryPerson?.firstName || "",
            lastname: primaryPerson?.lastName || "",
        },
        coborrower: {
            firstname: firstCoPerson?.firstName || "",
            lastname: firstCoPerson?.lastName || "",
        },
    }

    const subjectList = (subjects && subjects.length)
        ? subjects
        : subjectsFromParticipantsV2(Object.values(byId))
    const matchedRaw = matchSubjectFromTaskName(taskName, subjectList)
    const matched = matchedRaw && !matchedRaw.partyId
        ? (subjectList.find((s) => s.suffix === matchedRaw.suffix && s.partyId) || matchedRaw)
        : matchedRaw
    const task = `${taskName || ""}`

    if (matched?.partyId) {
        const legacyView = reduxViewForSubject(matched, viewApplication)
        if (matched.isPrimary || legacyView === "borrower") {
            return {
                showPrimaryColumn: true,
                showSecondaryColumn: false,
                primaryPartyId: matched.partyId,
                secondaryPartyId: "",
                primaryDisplayName: matched.firstName || "",
                secondaryDisplayName: "",
            }
        }
        if (legacyView === "coborrower") {
            return {
                showPrimaryColumn: false,
                showSecondaryColumn: true,
                primaryPartyId: primaryId,
                secondaryPartyId: matched.partyId,
                primaryDisplayName: "",
                secondaryDisplayName: matched.firstName || "",
            }
        }
        // Extra coborrower (3rd+): single column, borrower.* mapping → this partyId.
        return {
            showPrimaryColumn: true,
            showSecondaryColumn: false,
            primaryPartyId: matched.partyId,
            secondaryPartyId: "",
            primaryDisplayName: matched.firstName || "",
            secondaryDisplayName: "",
        }
    }

    const hidePrimary = !!(viewApplication.coborrower.firstname && task.includes(viewApplication.coborrower.firstname))
    const hideSecondary = task.includes(viewApplication.borrower.firstname)
    return {
        showPrimaryColumn: !hidePrimary,
        showSecondaryColumn: !hideSecondary && hasCo,
        primaryPartyId: primaryId,
        secondaryPartyId: hasCo ? firstCoId : "",
        primaryDisplayName: primaryPerson?.firstName || "",
        secondaryDisplayName: firstCoPerson?.firstName || "",
    }
}
